import {
  computed,
  ref,
  shallowRef,
  watch,
  onBeforeUnmount,
  useHost,
  type ComputedRef,
  type CSSProperties,
  type Ref,
  type ShallowRef,
  type ToRefs,
  type WritableComputedRef,
} from "vue";
import { useSharedState } from "composables/useSharedState";
import { useSlotPresence } from "composables/useSlotPresence";
import { useLayout } from "components/Layout/useLayout";
import { createEventDispatcher } from "@/runtime/shared/events";
import { usePosition } from "composables/usePosition";
import { useElementHover } from "@vueuse/core";
import { useTypeahead } from "composables/useTypeahead";
import { menuClasses } from "./menu.styles";
import { useLogoUrl } from "composables/useLogoUrl";
import { useAppearance } from "composables/useAppearance";
import { mergeClassProp } from "@/runtime/style-engine/define-variants";
import { animateIn, clearAnimationClasses, DISAPPEAR_ANIMATIONS } from "components/Floating/utils/floatingAnimation";
import {
  getDeepActiveElement,
  findMenuItemHost,
  getTopLevelItems,
  getSubItems,
  focusFirst,
  handleListNavigation,
} from "./useMenuKeyboard";
import type { MenuProps, MenuItemProps, FloatingItemData } from "./menu.types";
import type { ButtonProps } from "components/Button";

// ── Reactive route tracking (singleton) ─────────────────────────────────
let _routerBase = "";
let _currentPath: Ref<string> | null = null;
let _routeListenerCount = 0;
let _routeUpdateFn: (() => void) | null = null;
let _origPushState: typeof history.pushState | null = null;
let _origReplaceState: typeof history.replaceState | null = null;

const stripRouterBase = (pathname: string): string => {
  if (_routerBase && pathname.startsWith(_routerBase)) {
    return pathname.slice(_routerBase.length) || "/";
  }
  return pathname;
};

/**
 * Configure the base path prefix that `getCurrentPath()` strips from
 * `window.location.pathname`. Call once during app initialization,
 * before any `<e-menu>` connects.
 */
export const setRouterBase = (base: string): void => {
  _routerBase = base.replace(/\/$/, "");
  if (_currentPath && globalThis.window !== undefined) {
    _currentPath.value = stripRouterBase(globalThis.location.pathname);
  }
};

/** Lazily-initialized reactive ref that tracks `window.location.pathname`. */
export const getCurrentPath = (): Ref<string> => {
  if (!_currentPath) {
    _currentPath = ref(globalThis.window === undefined ? "/" : stripRouterBase(globalThis.location.pathname));
    if (globalThis.window !== undefined) {
      const update = () => {
        _currentPath!.value = stripRouterBase(globalThis.location.pathname);
      };
      _routeUpdateFn = update;
      _origPushState = history.pushState;
      _origReplaceState = history.replaceState;
      // `function` retained: history.pushState/replaceState patches must inherit `this` from the call site.
      history.pushState = function (...args: Parameters<History["pushState"]>) {
        _origPushState!.apply(this, args);
        update();
      };
      // `function` retained: history.pushState/replaceState patches must inherit `this` from the call site.
      history.replaceState = function (...args: Parameters<History["replaceState"]>) {
        _origReplaceState!.apply(this, args);
        update();
      };
      globalThis.addEventListener("popstate", update);
    }
  }
  _routeListenerCount++;
  onBeforeUnmount(() => {
    _routeListenerCount--;
    if (_routeListenerCount === 0 && _routeUpdateFn) {
      globalThis.removeEventListener("popstate", _routeUpdateFn);
      if (_origPushState) history.pushState = _origPushState;
      if (_origReplaceState) history.replaceState = _origReplaceState;
      _currentPath = null;
      _routeUpdateFn = null;
      _origPushState = null;
      _origReplaceState = null;
    }
  });
  return _currentPath;
};

/** Compare a `to` value against a pathname, ignoring trailing slashes and query/hash. */
export const matchesRoute = (to: string | undefined, path: string): boolean => {
  if (!to) return false;
  const a = to.split(/[?#]/)[0].replace(/\/$/, "") || "/";
  const b = path.replace(/\/$/, "") || "/";
  return a === b;
};

// ── Root mode types (Menu.vue) ─────────────────────────────────────────

export interface UseMenuOptions {
  props: Readonly<MenuProps<MenuItemProps>>;
  floatingContentRef?: Ref<HTMLElement | null>;
  floatingInnerRef?: Ref<HTMLElement | null>;
}

export interface UseMenuRootReturn {
  classes: ComputedRef<ReturnType<typeof menuClasses>>;
  collapsed: ComputedRef<boolean>;
  openProxy: WritableComputedRef<boolean>;
  toggleCollapse: () => void;
  toggleOpen: () => void;
  hasLeadingSlot: Ref<boolean>;
  hasTrailingSlot: Ref<boolean>;
  showLogo: ComputedRef<boolean>;
  collapsibleEnabled: ComputedRef<boolean>;
  collapsibleButtonProps: ComputedRef<ButtonProps | undefined>;
  floatingStyle: ComputedRef<CSSProperties>;
  floatingData: ShallowRef<FloatingItemData | null>;
  floatingVisible: Ref<boolean>;
  isPathActive: (to: string | undefined) => boolean;
  navigateFloatingItem: (event: MouseEvent, to: string | undefined, target: string | undefined) => void;
  logoUrl: ComputedRef<string>;
  appearanceIcon: ComputedRef<"sun" | "moon">;
  toggleAppearance: () => void;
  floatingPanelClasses: ComputedRef<{
    floatingTooltip: string;
    floatingHeader: string;
    floatingSubmenu: string;
    floatingSubmenuItem: string;
    floatingSubmenuIcon: string;
    floatingSubmenuContent: string;
  }>;
  handleFloatingNavigate: (event: Event) => void;
}

// ── Floating panel management ──────────────────────────────────────────

/** Mutable state shared between floating panel helper functions. */
interface FloatingPanelCtx {
  contentRef: Ref<HTMLElement | null>;
  innerRef: Ref<HTMLElement | null> | undefined;
  nullRef: Ref<HTMLElement | null>;
  data: ShallowRef<FloatingItemData | null>;
  visible: Ref<boolean>;
  trigger: ShallowRef<HTMLElement | null>;
  displayedHeight: Ref<number>;
  timers: {
    close: ReturnType<typeof setTimeout> | null;
    show: ReturnType<typeof setTimeout> | null;
    hide: ReturnType<typeof setTimeout> | null;
  };
  raf: { measure: number };
  anim: { current: Animation | null };
}

const fpCalcDuration = (distance: number): number => {
  return Math.round(Math.min(Math.max(distance * 0.5 + 80, 100), 400));
};

const fpMeasureNaturalHeight = (el: HTMLElement, innerRef: Ref<HTMLElement | null> | undefined): number => {
  const inner = innerRef?.value;
  if (inner) inner.style.minHeight = "0";
  const prev = el.style.height;
  el.style.height = "auto";
  const h = Math.round(el.getBoundingClientRect().height);
  el.style.height = prev;
  if (inner) inner.style.minHeight = "";
  return h;
};

const fpAnimateHeight = (ctx: FloatingPanelCtx, el: HTMLElement, target: number, duration: number): void => {
  if (ctx.anim.current) ctx.anim.current.cancel();
  const from = el.getBoundingClientRect().height;
  el.style.height = `${target}px`;
  if (Math.abs(from - target) < 1) return;
  ctx.anim.current = el.animate([{ height: `${from}px` }, { height: `${target}px` }], { duration, easing: "ease-out" });
  ctx.anim.current.onfinish = () => {
    ctx.anim.current = null;
  };
  ctx.anim.current.addEventListener("cancel", () => {
    ctx.anim.current = null;
  });
};

const fpClearTimers = (ctx: FloatingPanelCtx): void => {
  if (ctx.timers.show) {
    clearTimeout(ctx.timers.show);
    ctx.timers.show = null;
  }
  if (ctx.timers.hide) {
    clearTimeout(ctx.timers.hide);
    ctx.timers.hide = null;
  }
};

const fpClose = (ctx: FloatingPanelCtx): void => {
  fpClearTimers(ctx);
  if (ctx.timers.close) return;
  const el = (ctx.contentRef ?? ctx.nullRef).value;
  const duration = fpCalcDuration(ctx.displayedHeight.value);
  if (el) {
    fpAnimateHeight(ctx, el, 0, duration);
    clearAnimationClasses(el);
    el.classList.add(DISAPPEAR_ANIMATIONS.right);
  }
  ctx.displayedHeight.value = 0;
  ctx.timers.close = setTimeout(() => {
    ctx.timers.close = null;
    if (ctx.anim.current) {
      ctx.anim.current.cancel();
      ctx.anim.current = null;
    }
    ctx.visible.value = false;
    ctx.data.value = null;
    ctx.trigger.value = null;
    if (el) {
      el.style.height = "";
      clearAnimationClasses(el);
    }
  }, duration);
};

/** Measure and animate height transition in rAF callback. */
const fpMeasureAndAnimate = (ctx: FloatingPanelCtx, outer: HTMLElement): void => {
  ctx.raf.measure = 0;
  const fromH = outer.getBoundingClientRect().height;
  const toH = fpMeasureNaturalHeight(outer, ctx.innerRef);
  if (toH <= 0) {
    outer.style.height = `${fromH}px`;
    return;
  }
  const delta = Math.abs(toH - fromH);
  if (delta < 1) {
    outer.style.height = `${toH}px`;
    ctx.displayedHeight.value = toH;
    return;
  }
  outer.style.height = `${toH}px`;
  ctx.anim.current = outer.animate([{ height: `${fromH}px` }, { height: `${toH}px` }], {
    duration: fpCalcDuration(delta),
    easing: "ease-out",
  });
  const anim = ctx.anim.current;
  anim.onfinish = () => {
    if (ctx.anim.current !== anim) return;
    ctx.anim.current = null;
    const actual = fpMeasureNaturalHeight(outer, ctx.innerRef);
    if (actual > 0 && Math.abs(actual - toH) > 1) {
      outer.style.height = `${actual}px`;
      ctx.displayedHeight.value = actual;
    }
  };
  anim.addEventListener("cancel", () => {
    if (ctx.anim.current === anim) ctx.anim.current = null;
  });
  ctx.displayedHeight.value = toH;
};

const fpSwitchItem = (ctx: FloatingPanelCtx, detail: FloatingItemData): void => {
  const outer = (ctx.contentRef ?? ctx.nullRef).value;
  if (outer) clearAnimationClasses(outer);
  ctx.data.value = detail;
  ctx.trigger.value = detail.triggerElement;
  if (ctx.timers.show) {
    clearTimeout(ctx.timers.show);
    ctx.timers.show = null;
  }
  if (ctx.raf.measure) cancelAnimationFrame(ctx.raf.measure);
  if (ctx.anim.current) {
    const current = outer ? outer.getBoundingClientRect().height : 0;
    ctx.anim.current.cancel();
    ctx.anim.current = null;
    if (outer && current > 0) outer.style.height = `${current}px`;
  }
  ctx.raf.measure = requestAnimationFrame(() => {
    if (outer) fpMeasureAndAnimate(ctx, outer);
  });
};

const fpShowInitial = (ctx: FloatingPanelCtx, detail: FloatingItemData): void => {
  if (ctx.timers.show) clearTimeout(ctx.timers.show);
  ctx.timers.show = setTimeout(() => {
    ctx.timers.show = null;
    ctx.data.value = detail;
    ctx.trigger.value = detail.triggerElement;
    ctx.displayedHeight.value = 0;
    ctx.visible.value = true;
    const el = (ctx.contentRef ?? ctx.nullRef).value;
    if (el) {
      el.style.height = "0px";
      animateIn(el, "right");
    }
    ctx.raf.measure = requestAnimationFrame(() => {
      ctx.raf.measure = 0;
      if (!el) return;
      const toH = fpMeasureNaturalHeight(el, ctx.innerRef);
      if (toH <= 0) return;
      el.style.height = `${toH}px`;
      ctx.anim.current = el.animate([{ height: "0px" }, { height: `${toH}px` }], {
        duration: fpCalcDuration(toH),
        easing: "ease-out",
      });
      const a = ctx.anim.current;
      a.onfinish = () => {
        ctx.anim.current = null;
      };
      a.addEventListener("cancel", () => {
        if (ctx.anim.current === a) ctx.anim.current = null;
      });
      ctx.displayedHeight.value = toH;
    });
  }, 200);
};

const fpHandleShow = (ctx: FloatingPanelCtx, e: Event): void => {
  const detail = (e as CustomEvent<FloatingItemData>).detail;
  if (!detail?.triggerElement) return;
  if (ctx.timers.hide) {
    clearTimeout(ctx.timers.hide);
    ctx.timers.hide = null;
  }
  if (ctx.timers.close) {
    clearTimeout(ctx.timers.close);
    ctx.timers.close = null;
  }
  if (ctx.visible.value) fpSwitchItem(ctx, detail);
  else fpShowInitial(ctx, detail);
};

const fpHandleHide = (ctx: FloatingPanelCtx, e: Event): void => {
  const detail = (e as CustomEvent<{ triggerElement: HTMLElement }>).detail;
  if (detail?.triggerElement && ctx.trigger.value !== detail.triggerElement) return;
  if (ctx.timers.show) {
    clearTimeout(ctx.timers.show);
    ctx.timers.show = null;
  }
  if (ctx.visible.value) {
    ctx.timers.hide = setTimeout(() => {
      ctx.timers.hide = null;
      fpClose(ctx);
    }, 200);
  }
};

/** Create all floating panel state and handlers. */
const buildFloatingPanel = (
  floatingContentRef: Ref<HTMLElement | null> | undefined,
  floatingInnerRef: Ref<HTMLElement | null> | undefined,
  collapsed: ComputedRef<boolean>,
) => {
  const nullRef = ref<HTMLElement | null>(null);
  const ctx: FloatingPanelCtx = {
    contentRef: floatingContentRef ?? nullRef,
    innerRef: floatingInnerRef,
    nullRef,
    data: shallowRef<FloatingItemData | null>(null),
    visible: ref(false),
    trigger: shallowRef<HTMLElement | null>(null),
    displayedHeight: ref(0),
    timers: { close: null, show: null, hide: null },
    raf: { measure: 0 },
    anim: { current: null },
  };
  const { floatingStyles } = usePosition({
    triggerRef: ctx.trigger,
    floatingRef: ctx.contentRef,
    placement: "right-start",
    offset: 28,
    collisionStrategy: "shift",
    enabled: ctx.visible,
  });
  const isContentHovered = useElementHover(ctx.contentRef);
  watch(isContentHovered, (hovered) => {
    if (hovered) {
      if (ctx.timers.hide) {
        clearTimeout(ctx.timers.hide);
        ctx.timers.hide = null;
      }
    } else if (ctx.visible.value) {
      ctx.timers.hide = setTimeout(() => {
        ctx.timers.hide = null;
        fpClose(ctx);
      }, 200);
    }
  });
  watch(collapsed, (v) => {
    if (!v) fpClose(ctx);
  });
  return { ctx, floatingStyles };
};

/** Resolve the top-level menu item host from a focused element. */
const resolveTopLevelHost = (focusedHost: HTMLElement, items: HTMLElement[]): HTMLElement | null => {
  if (items.includes(focusedHost)) return focusedHost;
  const parent = focusedHost.parentElement as HTMLElement | null;
  return parent && items.includes(parent) ? parent : null;
};

/** Try to enter sub-items on ArrowDown from an open expandable item. Returns true if handled. */
const tryEnterSubItems = (event: KeyboardEvent, host: HTMLElement): boolean => {
  if (event.key !== "ArrowDown") return false;
  const details = host.shadowRoot?.querySelector("details");
  if (!details?.open) return false;
  const subItems = getSubItems(host);
  if (subItems.length === 0) return false;
  event.preventDefault();
  focusFirst(subItems);
  return true;
};

/** Build keyboard handler for the root menu. */
const buildMenuKeydownHandler = (host: Element | null, collapsed: ComputedRef<boolean>) => {
  const typeahead = useTypeahead();
  const handler = (e: Event) => {
    const event = e as KeyboardEvent;
    if (collapsed.value || !host) return;
    const deepActive = getDeepActiveElement();
    const focusedHost = deepActive ? findMenuItemHost(deepActive) : null;
    if (!focusedHost) return;
    const items = getTopLevelItems(host as HTMLElement);
    if (items.length === 0) return;
    const topLevelHost = resolveTopLevelHost(focusedHost, items);
    if (!topLevelHost) return;
    if (topLevelHost === focusedHost && tryEnterSubItems(event, focusedHost)) return;
    handleListNavigation(event, items, topLevelHost, typeahead);
  };
  return { handler, resetTypeahead: typeahead.resetTypeahead };
};

/** Navigate floating submenu item via SPA or native navigation. */
const navigateFloatingItemImpl = (
  event: MouseEvent,
  to: string | undefined,
  target: string | undefined,
  host: Element | null,
): void => {
  if (!to || target) return;
  event.preventDefault();
  if (host) {
    const menuItem = [...host.querySelectorAll("e-menu-item[to]")].find((el) => el.getAttribute("to") === to);
    if (menuItem) {
      (menuItem as HTMLElement).click();
      return;
    }
  }
  globalThis.location.href = to;
};

/** Wire up event listeners and cleanup for the root menu. */
const setupMenuHostListeners = (
  host: Element | null,
  ctx: FloatingPanelCtx,
  keyboard: ReturnType<typeof buildMenuKeydownHandler>,
): void => {
  if (!host) return;
  const onShow = (e: Event) => fpHandleShow(ctx, e);
  const onHide = (e: Event) => fpHandleHide(ctx, e);
  host.addEventListener("floating:show", onShow);
  host.addEventListener("floating:hide", onHide);
  host.addEventListener("keydown", keyboard.handler);

  onBeforeUnmount(() => {
    fpClearTimers(ctx);
    if (ctx.timers.close) {
      clearTimeout(ctx.timers.close);
      ctx.timers.close = null;
    }
    if (ctx.raf.measure) {
      cancelAnimationFrame(ctx.raf.measure);
      ctx.raf.measure = 0;
    }
    host.removeEventListener("floating:show", onShow);
    host.removeEventListener("floating:hide", onHide);
    host.removeEventListener("keydown", keyboard.handler);
    keyboard.resetTypeahead();
  });
};

// ── Root mode implementation ───────────────────────────────────────────

/** Build the writable open proxy for the menu. */
const buildMenuOpenProxy = (
  state: ToRefs<MenuProps<MenuItemProps>>,
  dispatchEvent: ReturnType<typeof createEventDispatcher>,
): WritableComputedRef<boolean> => {
  return computed({
    get: () => state.open?.value ?? false,
    set: (v: boolean) => {
      dispatchEvent("update:open", v);
    },
  });
};

const buildFloatingStyle = (floatingStyles: ComputedRef<CSSProperties>): ComputedRef<CSSProperties> => {
  return computed<CSSProperties>(() => ({
    ...floatingStyles.value,
    overflow: "hidden",
    borderRadius: "0.5rem",
    zIndex: 50,
    pointerEvents: "auto",
  }));
};

/** Build logo + collapsible-button computeds derived from state. */
const buildCollapsibleAndLogo = (
  state: ToRefs<MenuProps<MenuItemProps>>,
): {
  showLogo: ComputedRef<boolean>;
  collapsibleEnabled: ComputedRef<boolean>;
  collapsibleButtonProps: ComputedRef<ButtonProps | undefined>;
} => {
  const showLogo = computed(() => !!state.logo?.value);
  const collapsibleEnabled = computed(() => !!state.collapsible?.value);
  const collapsibleButtonProps = computed<ButtonProps | undefined>(() => {
    const value = state.collapsible?.value;
    return typeof value === "object" && value !== null ? (value as ButtonProps) : undefined;
  });
  return { showLogo, collapsibleEnabled, collapsibleButtonProps };
};

/** Build the root `classes` computed. */
const buildMenuRootClasses = (
  props: Readonly<MenuProps<MenuItemProps>>,
  collapsed: ComputedRef<boolean>,
  openProxy: WritableComputedRef<boolean>,
): ComputedRef<ReturnType<typeof menuClasses>> => {
  return computed(() =>
    mergeClassProp(
      menuClasses({
        orientation: "vertical",
        collapsed: collapsed.value ? "true" : "false",
        open: openProxy.value ? "true" : "false",
      }),
      props.class,
    ),
  );
};

/** Bundle the floating-panel navigation handlers + the panel-only class subset. */
const buildFloatingNavigation = (host: HTMLElement | null, classes: ComputedRef<ReturnType<typeof menuClasses>>) => {
  const navigateFloatingItem = (event: MouseEvent, to: string | undefined, target: string | undefined): void =>
    navigateFloatingItemImpl(event, to, target, host);

  const floatingPanelClasses = computed(() => ({
    floatingTooltip: classes.value.floatingTooltip,
    floatingHeader: classes.value.floatingHeader,
    floatingSubmenu: classes.value.floatingSubmenu,
    floatingSubmenuItem: classes.value.floatingSubmenuItem,
    floatingSubmenuIcon: classes.value.floatingSubmenuIcon,
    floatingSubmenuContent: classes.value.floatingSubmenuContent,
  }));

  const handleFloatingNavigate = (event: Event): void => {
    const { originalEvent, to, target } = (
      event as CustomEvent<{ originalEvent: MouseEvent; to?: string; target?: string }>
    ).detail;
    navigateFloatingItem(originalEvent, to, target);
  };

  return { navigateFloatingItem, floatingPanelClasses, handleFloatingNavigate };
};

/**
 * Composable for the Menu parent component (full menu behavior).
 * For MenuItem and other child slots, use `useMenuCoordination` from `./useMenu.Coordination`.
 */
export const useMenu = (options: UseMenuOptions): UseMenuRootReturn => {
  const { props, floatingContentRef, floatingInnerRef } = options;

  const host = useHost();
  const state = useSharedState(props);
  const dispatchEvent = createEventDispatcher(host);

  const { toggleCollapsed, collapsed } = useLayout();
  const { leading: hasLeadingSlot, trailing: hasTrailingSlot } = useSlotPresence(["leading", "trailing"]);
  const { logoUrl } = useLogoUrl({ icon: collapsed });
  const { icon: appearanceIcon, toggle: toggleAppearance } = useAppearance();
  const routePath = getCurrentPath();

  const openProxy = buildMenuOpenProxy(state, dispatchEvent);
  const { showLogo, collapsibleEnabled, collapsibleButtonProps } = buildCollapsibleAndLogo(state);
  const classes = buildMenuRootClasses(props, collapsed, openProxy);

  const { ctx: fpCtx, floatingStyles } = buildFloatingPanel(floatingContentRef, floatingInnerRef, collapsed);
  const floatingStyle = buildFloatingStyle(floatingStyles);

  const keyboard = buildMenuKeydownHandler(host, collapsed);
  setupMenuHostListeners(host, fpCtx, keyboard);

  watch(routePath, () => {
    if (openProxy.value) openProxy.value = false;
  });

  const floating = buildFloatingNavigation(host, classes);

  return {
    classes,
    collapsed,
    openProxy,
    toggleCollapse: toggleCollapsed,
    toggleOpen: () => {
      openProxy.value = !openProxy.value;
    },
    hasLeadingSlot,
    hasTrailingSlot,
    showLogo,
    collapsibleEnabled,
    collapsibleButtonProps,
    floatingStyle,
    floatingData: fpCtx.data,
    floatingVisible: fpCtx.visible,
    isPathActive: (to) => matchesRoute(to, routePath.value),
    logoUrl,
    appearanceIcon,
    toggleAppearance,
    navigateFloatingItem: floating.navigateFloatingItem,
    floatingPanelClasses: floating.floatingPanelClasses,
    handleFloatingNavigate: floating.handleFloatingNavigate,
  };
};
